import { Link } from "react-router-dom";
import { ArrowRight, Tag } from "lucide-react";

export const BundleCard = ({ bundle }) => {
  const isMix = bundle.bundle_type === "mix_match";
  const isKit = bundle.bundle_type === "kit";
  const fixedSaving = isMix && bundle.min_normal_total === bundle.max_normal_total;
  const saving = (isMix || isKit) ? (bundle.saving_up_to || 0) : (bundle.saving || 0);
  const normal = isKit ? (bundle.compare_at_price ?? bundle.max_normal_total ?? 0) : isMix ? (bundle.max_normal_total || 0) : (bundle.normal_total || 0);
  const includes = isKit
    ? (bundle.kit_groups || []).map((g) => g.label || g.role || "Item").join(" + ") || "1 Jersey + 1 Short"
    : isMix
    ? `Pick any ${bundle.choose_quantity} — ${(bundle.eligible_products || []).map((i) => i.name).join(" / ")}`
    : (bundle.items || []).map((i) => `${i.quantity > 1 ? i.quantity + "× " : ""}${i.name}`).join(" + ");

  // Never show an empty placeholder when a valid eligible product image exists. For the kit
  // there is no combined jersey+shorts photo, so fall back to an eligible rugby jersey's image.
  const kitJerseyGroup = (bundle.kit_groups || []).find((g) => (g.role || "").toLowerCase().includes("jersey")) || (bundle.kit_groups || [])[0];
  const fallbackImg = isKit
    ? (kitJerseyGroup?.products || []).find((p) => p.image_url)?.image_url
      || (bundle.kit_groups || []).flatMap((g) => g.products || []).find((p) => p.image_url)?.image_url
    : isMix
    ? (bundle.eligible_products || []).find((p) => p.image_url)?.image_url
    : (bundle.items || []).find((i) => i.image_url)?.image_url;
  const displayImg = bundle.image_url || fallbackImg || "";

  return (
    <Link
      to={`/bundles/${bundle.slug}`}
      data-testid={`bundle-card-${bundle.slug}`}
      className="group block bg-[#171717] border border-[#262626] hover:border-[#F7931E] transition-colors overflow-hidden"
    >
      <div className="relative aspect-[4/3] bg-[#0A0A0A] overflow-hidden">
        {displayImg ? (
          <img
            src={displayImg}
            alt={bundle.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#525252]">
            <Tag size={40} />
          </div>
        )}
        {saving > 0 && (
          <span
            data-testid={`bundle-save-badge-${bundle.slug}`}
            className="absolute top-3 left-3 bg-[#F7931E] text-black text-xs font-black uppercase tracking-wider px-3 py-1"
          >
            Save {isMix && !fixedSaving ? "up to " : ""}R{Math.round(saving)}
          </span>
        )}
        {isMix && (
          <span className="absolute top-3 right-3 bg-black/70 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">
            Mix &amp; Match
          </span>
        )}
        {isKit && (
          <span className="absolute top-3 right-3 bg-black/70 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">
            Complete Kit
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-white font-black uppercase tracking-tight text-lg font-heading leading-tight">
          {bundle.name}
        </h3>
        {bundle.headline && (
          <p className="text-[#F7931E] text-xs font-bold uppercase tracking-wider mt-1">{bundle.headline}</p>
        )}
        <p className="text-[#A3A3A3] text-xs mt-2 line-clamp-2">{includes}</p>
        <div className="flex items-end gap-3 mt-4">
          <span className="text-[#F7931E] font-black text-2xl">R{bundle.bundle_price.toFixed(0)}</span>
          {normal > bundle.bundle_price && (
            <span className="text-[#737373] line-through text-sm mb-1">{isKit ? "Was " : ""}R{normal.toFixed(0)}</span>
          )}
        </div>
        <span className="inline-flex items-center gap-2 text-white font-bold uppercase tracking-wide text-xs mt-4 group-hover:text-[#F7931E] transition-colors">
          Shop this bundle <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
};

export default BundleCard;
